<template>
    <div :class="props.customClass" :data-cy="props.dataCy">
        <div ref="phoneInputWrapper"
             class="phone-input-wrapper tw:relative tw:w-full tw:border tw:rounded-md"
             :style="{ '--country-list-width': countryListWidth }"
             :class="[
                 isFocused
                     ? 'tw:border-blue-400'
                     : (hasError ? 'phone-input-wrapper--error tw:border-red-400 tw:bg-red-50/50' : 'tw:border-slate-300')
             ]"
        >
            <VuePhoneNumberInput
                v-model="localPhone"
                :disabled="props.disabled"
                :default-country-code="props.defaultCountryCode"
                :translations="translations"
                size="lg"
                @update="handleUpdate"
                @phone-number-focused="isFocused = true"
                @phone-number-blur="isFocused = false; tocado = true"
            />
            <span
                v-if="props.label"
                class="tw:absolute tw:uppercase tw:duration-200 tw:tracking-wide tw:origin-left tw:z-10 tw:pointer-events-none tw:px-1"
                :class="[
                    (isFocused || hasValue)
                        ? 'tw:left-[106px] tw:-top-[11px] tw:scale-75 tw:bg-white tw:font-bold'
                        : 'tw:left-[110px] tw:top-1/2 tw:-translate-y-1/2 tw:scale-100 tw:bg-transparent',
                    isFocused
                        ? 'tw:text-blue-800 tw:font-bold'
                        : (hasError ? 'tw:text-red-400' : 'tw:text-slate-600')
                ]"
            >
                {{ props.label }}
            </span>
        </div>
        <span
            v-if="mostrarErrorFormato"
            class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
        >
            {{ $t('formularios.formato_telefono') }}
        </span>
        <span
            v-else-if="mostrarErrorObligatorio"
            class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
        >
            {{ $t('formularios.telefono_obligatorio') }}
        </span>
    </div>
</template>

<script setup>
    import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from 'vue';
    import VuePhoneNumberInput from 'vue-phone-number-input';
    import 'vue-phone-number-input/dist/vue-phone-number-input.css';

    const instance = getCurrentInstance();
    const $t = instance.proxy.$t.bind(instance.proxy);

    const props = defineProps({
        label: String,
        value: {
            type: [String, Number],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        forceValidate: {
            type: Boolean,
            default: false
        },
        defaultCountryCode: {
            type: String,
            default: 'ES'
        },
        customClass: String,
        dataCy: String
    });

    const emit = defineEmits(['input', 'valid']);

    const toE164Display = val => {
        if (!val) return null;
        const str = String(val);
        return str.startsWith('+') ? str : `+${str}`;
    };

    const localPhone = ref(toE164Display(props.value));
    const isValid = ref(!!props.value);
    const tocado = ref(false);
    const isFocused = ref(false);
    const phoneInputWrapper = ref(null);
    const countryListWidth = ref('100%');

    let resizeObserver = null;

    const translations = {
        countrySelectorLabel: $t('formularios.codigo_pais'),
        phoneNumberLabel: ' ',
        example: $t('formularios.telefono_ejemplo')
    };

    const hasValue = computed(() => !!localPhone.value);

    const mostrarErrorObligatorio = computed(() =>
        props.required && (tocado.value || props.forceValidate) && !localPhone.value
    );

    const mostrarErrorFormato = computed(() =>
        !!localPhone.value && !isValid.value
    );

    const hasError = computed(() => mostrarErrorObligatorio.value || mostrarErrorFormato.value);

    let emittingUpdate = false;

    const handleUpdate = infoNumber => {
        isValid.value = infoNumber.isValid;
        const hasNumber = !!infoNumber.nationalNumber;
        const formatted = hasNumber
            ? (infoNumber.countryCallingCode + infoNumber.nationalNumber) || null
            : null;

        emittingUpdate = true;
        emit('input', formatted);

        if (!hasNumber) {
            emit('valid', !props.required);
        } else {
            emit('valid', isValid.value);
        }
    };

    watch(() => props.value, (newVal, oldVal) => {
        if (emittingUpdate) {
            emittingUpdate = false;
            return;
        }
        if (newVal === oldVal) return;
        localPhone.value = toE164Display(newVal);
    });

    const updateCountryListWidth = () => {
        if (!phoneInputWrapper.value) {
            return;
        }

        countryListWidth.value = `${phoneInputWrapper.value.getBoundingClientRect().width}px`;
    };

    onMounted(() => {
        updateCountryListWidth();

        const hasNumber = !!localPhone.value;
        const valid = props.required ? (hasNumber && isValid.value) : true;
        emit('valid', valid);

        if (typeof ResizeObserver === 'undefined' || !phoneInputWrapper.value) {
            return;
        }

        resizeObserver = new ResizeObserver(() => {
            updateCountryListWidth();
        });

        resizeObserver.observe(phoneInputWrapper.value);
    });

    onUnmounted(() => {
        resizeObserver?.disconnect();
    });
</script>

<script>
    export default {
        inheritAttrs: false
    };
</script>

<style scoped>
    /* Wrapper con alto total fijo (incluye borde) */
    .phone-input-wrapper {
        height: 38px;
        box-sizing: border-box;
    }

    /* Remove library borders - wrapper will have the border */
    .phone-input-wrapper :deep(.vue-phone-number-input) {
        border-radius: 0.3rem;
        overflow: visible;
        height: 100%;
    }

    /* Fuerza el mismo ancho del bloque de país que el selector visual */
    .phone-input-wrapper :deep(.select-country-container) {
        flex: 0 0 100px !important;
        width: 100px !important;
        min-width: 100px !important;
        max-width: 100px !important;
    }

    /* Country selector - no border (wrapper has it) */
    .phone-input-wrapper :deep(.country-selector) {
        background-color: #ffffff;
        height: 100% !important;
        min-height: 100% !important;
        width: 100px !important;
        border-radius: 0.3rem !important;

    }

    /* Input visible del selector de país (bandera + prefijo) */
    .phone-input-wrapper :deep(.country-selector__input) {
        border: none !important;
        border-right: 1px solid #cad5e2 !important;
        background-color: transparent !important;
        color: #475569;
        cursor: pointer;
        padding: 0 !important;
        padding-left: 38px !important;
        height: 100% !important;
        min-height: 100% !important;
    }

    /* El input readonly del país no debe mostrar el foco azul propio de la librería */
    .phone-input-wrapper :deep(.country-selector__input:focus),
    .phone-input-wrapper :deep(.country-selector.is-focused .country-selector__input) {
        border: none !important;
        border-right: 1px solid #cad5e2 !important;
        box-shadow: none !important;
        outline: none !important;
    }

    /* Oculta el label interno porque el componente usa label flotante propio */
    .phone-input-wrapper :deep(.country-selector__label) {
        display: none !important;
    }

    /* El foco visual lo maneja el wrapper externo */
    .phone-input-wrapper :deep(.country-selector.is-focused) {
        box-shadow: none !important;
    }

    /* Centra la bandera del país a mitad de altura del input */
    .phone-input-wrapper :deep(.country-selector__country-flag) {
        top: 50% !important;
        transform: translateY(-50%);
        margin: 0;
    }

    /* Evita desplazamientos extra del sprite de bandera */
    .phone-input-wrapper :deep(.country-selector__country-flag img) {
        position: static !important;
        display: block;
    }

    /* Centra el toggle del desplegable a mitad de altura */
    .phone-input-wrapper :deep(.country-selector__toggle) {
        height: auto;
        display: flex;
        align-items: center;
        bottom: 7px !important;
    }

    /* Mantiene centrado el icono SVG de la flecha */
    .phone-input-wrapper :deep(.country-selector__toggle__arrow) {
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        padding: 0 !important;
    }

    /* Phone number input - no border (wrapper has it) */
    /* Contenedor del input telefónico sin borde interno */
    .phone-input-wrapper :deep(.input-tel) {
        border: none !important;
        background-color: transparent !important;
        height: 100% !important;
        min-height: 100% !important;
        padding-left: 8px;

    }

    /* Campo de teléfono: color, espaciado y altura consistentes */
    .phone-input-wrapper :deep(.input-tel__input) {
        border: none !important;
        background-color: transparent !important;
        color: #0f172a;
        outline: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        height: 100% !important;
        min-height: 100% !important;
    }

    /* Oculta el label interno del input, reemplazado por label flotante */
    .phone-input-wrapper :deep(.input-tel__label) {
        display: none !important;
    }

    /* Color de texto/caret cuando el input tiene foco */
    .phone-input-wrapper :deep(.input-tel.is-focused .input-tel__input) {
        color: #60a5fa;
        caret-color: #60a5fa;
    }

    /* Remove valid state styling */
    .phone-input-wrapper :deep(.input-tel.is-valid .input-tel__input),
    .phone-input-wrapper :deep(.input-tel.is-valid .input-tel__input:hover) {
        border-color: transparent !important;
        box-shadow: none !important;
    }

    /* Error state variants */
    /* Fondo del selector de país en estado de error */
    .phone-input-wrapper--error :deep(.country-selector) {
        background-color: rgba(254, 242, 242, 0.5);
        height: 100% !important;
        min-height: 100% !important;
    }

    /* Fondo del input telefónico en estado de error */
    .phone-input-wrapper--error :deep(.input-tel__input) {
        background-color: rgba(254, 242, 242, 0.5);
    }

    /* Lista desplegable de países: queda por encima de modales y overlays */
    .phone-input-wrapper :deep(.country-selector__list) {
        width: var(--country-list-width, 100%) !important;
        min-width: var(--country-list-width, 100%) !important;
        z-index: 1000;
        max-height: 240px;
        margin-top: 3px;
        border: 1px solid #cbd5e1;
        border-radius: 0.3rem !important;
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
        overflow-x: hidden;
        overflow-y: auto;
    }

    /* El dropdown acompaña el color del borde exterior cuando hay error */
    .phone-input-wrapper--error :deep(.country-selector__list) {
        border-color: #fca5a5;
        border-radius: 0.3rem !important;
    }

    /* Item de país en la lista: mantiene altura y alineación uniforme */
    .phone-input-wrapper :deep(.country-selector__list__item) {
        min-height: 36px;
        display: flex;
        align-items: center;
        border-radius: 0.3rem !important;
    }

    /* País seleccionado */
    .phone-input-wrapper :deep(.country-selector__list__item.selected) {
        background-color: var(--altcolor) !important;
        color: #ffffff !important;
        border-radius: 0.3rem !important;
    }

    /* Estado hover/focus de item para mejorar legibilidad */
    .phone-input-wrapper :deep(.country-selector__list__item:hover),
    .phone-input-wrapper :deep(.country-selector__list__item:focus) {
        background-color: #ffffff;
        border: 1px solid var(--altcolor) !important;
        border-radius: 0.3rem !important;
        color: var(--altcolor);
    }

    /* Nombre del país con corte elegante cuando el texto es largo */
    .phone-input-wrapper :deep(.country-selector__list__country-name) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>
